<script setup>
import { computed, h, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/button/ButtonGroup.vue';
import ButtonGroupItem from 'dashboard/components-next/button/ButtonGroupItem.vue';
import Icon from 'next/icon/Icon.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useOperators } from 'dashboard/components-next/filter/operators';
import {
  useEventFilterTypes,
  EVENT_CUSTOM_ATTRIBUTE_TYPE,
  isEventCustomAttributeKey,
} from './useEventFilterTypes';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  agents: {
    type: Array,
    default: () => [],
  },
  agentBots: {
    type: Array,
    default: () => [],
  },
  eventLabels: {
    type: Array,
    default: () => [],
  },
  currentUser: {
    type: Object,
    default: null,
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
  /** `group` matches calendar ButtonGroup (spaces / day / week) text colors. */
  triggerVariant: {
    type: String,
    default: 'default',
    validator: value => ['default', 'group'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue', 'apply', 'close']);

const PRESENCE_FILTER_OPERATORS = ['is_present', 'is_not_present'];

const { t } = useI18n();
const { equalityOperators } = useOperators();
const { customAttributeFilterTypes, eventAttributeKeys } =
  useEventFilterTypes();

const open = ref(false);
const draftFilters = ref([]);
const conditionsRef = useTemplateRef('conditionsRef');
const lastAppliedQuickFilter = ref(null);
const syncingQuickFilter = ref(false);
const rootRef = ref(null);
const panelRef = ref(null);
const panelStyle = ref({});

const VIEWPORT_GUTTER = 8;
let contentBoundsObserver = null;

const QUICK_FILTER_KEYS = [
  'unread',
  'unassigned',
  'assigned_to_me',
  'hide_drafts',
];

const DEFAULT_FILTER = () => ({
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: {},
  queryOperator: 'and',
});

const eventOperators = computed(() =>
  equalityOperators.value.map(op => ({
    ...op,
    label:
      op.value === 'equal_to'
        ? t('PLANNING.EVENTS.FILTERS.OPERATORS.IS')
        : t('PLANNING.EVENTS.FILTERS.OPERATORS.IS_NOT'),
  }))
);

const builtInFilterTypes = computed(() => [
  {
    attributeKey: 'status',
    value: 'status',
    attributeName: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.STAGE'),
    label: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.STAGE'),
    inputType: 'multiSelect',
    options: props.statusOptions.map(option => ({
      id: option.key,
      name: option.label,
    })),
    dataType: 'text',
    filterOperators: eventOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'event_manager_id',
    value: 'event_manager_id',
    attributeName: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.EVENT_MANAGER'),
    label: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.EVENT_MANAGER'),
    inputType: 'searchSelect',
    options: [
      {
        id: 'null',
        name: t('PLANNING.EVENTS.FILTERS.UNASSIGNED'),
      },
      ...props.agents.map(agent => ({
        id: agent.id,
        name: agent.name,
        icon: h(Avatar, {
          src: agent.thumbnail,
          name: agent.name,
          size: 20,
          roundedFull: true,
        }),
      })),
    ],
    dataType: 'text',
    filterOperators: eventOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'collaborator_id',
    value: 'collaborator_id',
    attributeName: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.COLLABORATOR'),
    label: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.COLLABORATOR'),
    inputType: 'searchSelect',
    options: props.agents.map(agent => ({
      id: agent.id,
      name: agent.name,
      icon: h(Avatar, {
        src: agent.thumbnail,
        name: agent.name,
        size: 20,
        roundedFull: true,
      }),
    })),
    dataType: 'text',
    filterOperators: eventOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'assigned_to_user_id',
    value: 'assigned_to_user_id',
    attributeName: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.ASSIGNED_TO'),
    label: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.ASSIGNED_TO'),
    inputType: 'searchSelect',
    options: props.agents.map(agent => ({
      id: agent.id,
      name: agent.name,
      icon: h(Avatar, {
        src: agent.thumbnail,
        name: agent.name,
        size: 20,
        roundedFull: true,
      }),
    })),
    dataType: 'text',
    filterOperators: eventOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'event_label_id',
    value: 'event_label_id',
    attributeName: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.EVENT_LABEL'),
    label: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.EVENT_LABEL'),
    inputType: 'multiSelect',
    options: [
      {
        id: 'null',
        name: t('EVENT_LABEL_MGMT.DROPDOWN.NO_LABEL'),
      },
      ...props.eventLabels.map(label => ({
        id: label.id,
        name: label.title,
        icon: h('span', {
          class: 'inline-block size-2.5 shrink-0 rounded-full',
          style: { backgroundColor: label.color },
        }),
      })),
    ],
    dataType: 'text',
    filterOperators: eventOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'unread',
    value: 'unread',
    attributeName: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.UNREAD'),
    label: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.UNREAD'),
    inputType: 'booleanSelect',
    options: [],
    dataType: 'text',
    filterOperators: eventOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'created_by',
    value: 'created_by',
    attributeName: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.CREATED_BY'),
    label: t('PLANNING.EVENTS.FILTERS.ATTRIBUTES.CREATED_BY'),
    inputType: 'searchSelect',
    options: [
      {
        id: 'any',
        name: t('PLANNING.EVENTS.FILTERS.ANY_ASSISTANT'),
      },
      {
        id: 'null',
        name: t('PLANNING.EVENTS.FILTERS.NOT_CREATED_BY_ASSISTANT'),
      },
      ...(props.agentBots.length
        ? [
            {
              id: '__header_assistants',
              name: t('PLANNING.EVENTS.FILTERS.GROUPS.ASSISTANTS'),
              disabled: true,
            },
            ...props.agentBots.map(bot => ({
              id: `AgentBot:${bot.id}`,
              name: bot.name,
              icon: h(Avatar, {
                src: bot.thumbnail,
                name: bot.name,
                size: 20,
                roundedFull: true,
              }),
            })),
          ]
        : []),
      ...(props.agents.length
        ? [
            {
              id: '__header_users',
              name: t('PLANNING.EVENTS.FILTERS.GROUPS.USERS'),
              disabled: true,
            },
            ...props.agents.map(agent => ({
              id: `User:${agent.id}`,
              name: agent.name,
              icon: h(Avatar, {
                src: agent.thumbnail,
                name: agent.name,
                size: 20,
                roundedFull: true,
              }),
            })),
          ]
        : []),
    ],
    dataType: 'text',
    filterOperators: eventOperators.value,
    attributeModel: 'standard',
  },
]);

const filterTypes = computed(() => [
  ...builtInFilterTypes.value,
  ...customAttributeFilterTypes.value,
]);

const quickFilterOptions = computed(() => [
  {
    key: 'unread',
    label: t('PLANNING.EVENTS.FILTERS.QUICK_FILTERS.UNREAD'),
  },
  {
    key: 'unassigned',
    label: t('PLANNING.EVENTS.FILTERS.QUICK_FILTERS.UNASSIGNED'),
  },
  {
    key: 'assigned_to_me',
    label: t('PLANNING.EVENTS.FILTERS.QUICK_FILTERS.ASSIGNED_TO_ME'),
  },
  {
    key: 'hide_drafts',
    label: t('PLANNING.EVENTS.FILTERS.QUICK_FILTERS.HIDE_DRAFTS'),
  },
]);

const appliedCount = computed(() => props.modelValue.length);

const getFilterValueId = filter => {
  const { values } = filter;
  if (values && typeof values === 'object' && !Array.isArray(values)) {
    return values.id;
  }
  return values;
};

const statusValueIds = values => {
  if (Array.isArray(values)) {
    return values
      .map(value => (value && typeof value === 'object' ? value.id : value))
      .filter(id => id != null && id !== '');
  }
  if (values && typeof values === 'object') {
    return values.id != null ? [values.id] : [];
  }
  if (values != null && values !== '') return [values];
  return [];
};

const MULTI_SELECT_KEYS = ['status', 'event_label_id'];

// Saved views and stage routes still store one {id, name}. MultiSelect needs an array.
const coerceFilterForEditor = filter => {
  const next = { ...filter };
  if (
    !MULTI_SELECT_KEYS.includes(next.attributeKey) ||
    Array.isArray(next.values)
  ) {
    return next;
  }

  const ids = statusValueIds(next.values);
  const single = next.values;
  next.values = ids.map(id => ({
    id,
    name: single && typeof single === 'object' ? single.name : id,
  }));
  return next;
};

const matchesQuickFilter = (filter, key) => {
  const valueId = getFilterValueId(filter);

  switch (key) {
    case 'unread':
      return (
        filter.attributeKey === 'unread' &&
        filter.filterOperator === 'equal_to' &&
        (valueId === true || valueId === 'true')
      );
    case 'unassigned':
      return (
        filter.attributeKey === 'event_manager_id' &&
        filter.filterOperator === 'equal_to' &&
        (valueId === 'null' || valueId === 'unassigned')
      );
    case 'assigned_to_me':
      return (
        filter.attributeKey === 'assigned_to_user_id' &&
        filter.filterOperator === 'equal_to' &&
        props.currentUser?.id != null &&
        String(valueId) === String(props.currentUser.id)
      );
    case 'hide_drafts': {
      const ids = statusValueIds(filter.values);
      return (
        filter.attributeKey === 'status' &&
        filter.filterOperator === 'not_equal_to' &&
        ids.length === 1 &&
        ids[0] === 'draft'
      );
    }
    default:
      return false;
  }
};

const exactlyMatchesQuickFilter = (filters, key) =>
  filters.length === 1 && matchesQuickFilter(filters[0], key);

const findMatchingQuickFilterKey = filters =>
  QUICK_FILTER_KEYS.find(key => exactlyMatchesQuickFilter(filters, key)) ||
  null;

const isQuickFilterActive = key =>
  lastAppliedQuickFilter.value === key &&
  exactlyMatchesQuickFilter(draftFilters.value, key);

const hasCompleteValue = filter => {
  if (PRESENCE_FILTER_OPERATORS.includes(filter.filterOperator)) return true;

  const values = filter.values;
  if (Array.isArray(values)) return values.length > 0;
  if (values && typeof values === 'object') return values.id != null;
  return values != null && values !== '';
};

const buildQuickFilterRule = key => {
  switch (key) {
    case 'unread':
      return {
        attributeKey: 'unread',
        filterOperator: 'equal_to',
        values: { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
        queryOperator: 'and',
      };
    case 'unassigned':
      return {
        attributeKey: 'event_manager_id',
        filterOperator: 'equal_to',
        values: {
          id: 'null',
          name: t('PLANNING.EVENTS.FILTERS.UNASSIGNED'),
        },
        queryOperator: 'and',
      };
    case 'assigned_to_me':
      return {
        attributeKey: 'assigned_to_user_id',
        filterOperator: 'equal_to',
        values: {
          id: props.currentUser.id,
          name: props.currentUser.name,
          icon: h(Avatar, {
            src: props.currentUser.avatar_url || props.currentUser.thumbnail,
            name: props.currentUser.name,
            size: 20,
            roundedFull: true,
          }),
        },
        queryOperator: 'and',
      };
    case 'hide_drafts': {
      const draftOption = props.statusOptions.find(
        option => option.key === 'draft'
      );
      return {
        attributeKey: 'status',
        filterOperator: 'not_equal_to',
        values: {
          id: 'draft',
          name: draftOption?.label || t('PLANNING.EVENTS.STAGES.DRAFT'),
        },
        queryOperator: 'and',
      };
    }
    default:
      return null;
  }
};

const stampCustomAttributeType = filter => {
  const next = { ...filter };
  if (isEventCustomAttributeKey(next.attributeKey, eventAttributeKeys.value)) {
    next.customAttributeType = EVENT_CUSTOM_ATTRIBUTE_TYPE;
  } else {
    delete next.customAttributeType;
  }
  return next;
};

const toAppliedFilters = filters =>
  filters
    .map(filter =>
      stampCustomAttributeType({
        ...filter,
        queryOperator: filter.queryOperator === 'or' ? 'or' : 'and',
      })
    )
    .filter(hasCompleteValue);

const emitApplied = filters => {
  const applied = toAppliedFilters(filters);
  emit('update:modelValue', applied);
  emit('apply', applied);
};

const syncDraftFromApplied = () => {
  syncingQuickFilter.value = true;
  draftFilters.value = props.modelValue.length
    ? props.modelValue.map(filter => coerceFilterForEditor(filter))
    : [DEFAULT_FILTER()];
  nextTick(() => {
    syncingQuickFilter.value = false;
  });
};

const syncQuickFilterHighlightFromApplied = () => {
  lastAppliedQuickFilter.value = findMatchingQuickFilterKey(props.modelValue);
};

watch(
  () => props.modelValue,
  () => {
    if (!open.value) {
      syncDraftFromApplied();
      syncQuickFilterHighlightFromApplied();
    }
  },
  { immediate: true, deep: true }
);

watch(
  draftFilters,
  () => {
    if (syncingQuickFilter.value) return;
    if (!open.value) return;
    lastAppliedQuickFilter.value = null;
  },
  { deep: true }
);

const toggle = () => {
  if (!open.value) {
    syncDraftFromApplied();
    syncQuickFilterHighlightFromApplied();
  }
  open.value = !open.value;
};

const getContentBounds = trigger => {
  // `main` sits beside the app sidebar, so its rect already reflects
  // collapsed / expanded / resized sidebar state (and mobile overlay).
  const content = trigger.closest('main');
  const contentRect = content?.getBoundingClientRect();
  const viewportLeft = VIEWPORT_GUTTER;
  const viewportRight = window.innerWidth - VIEWPORT_GUTTER;

  return {
    left: Math.max(viewportLeft, (contentRect?.left ?? 0) + VIEWPORT_GUTTER),
    right: Math.min(
      viewportRight,
      (contentRect?.right ?? window.innerWidth) - VIEWPORT_GUTTER
    ),
  };
};

const updateDropdownPosition = () => {
  const trigger = rootRef.value;
  const panel = panelRef.value;
  if (!trigger || !panel || !open.value) return;

  const triggerRect = trigger.getBoundingClientRect();
  const bounds = getContentBounds(trigger);
  const availableWidth = Math.max(0, bounds.right - bounds.left);
  const panelWidth = Math.min(panel.offsetWidth, availableWidth);

  // Prefer left-align with the trigger, then nudge just enough to stay on screen.
  let left = triggerRect.left;
  if (left + panelWidth > bounds.right) {
    left = bounds.right - panelWidth;
  }
  if (left < bounds.left) {
    left = bounds.left;
  }

  panelStyle.value = {
    top: `${triggerRect.bottom + VIEWPORT_GUTTER}px`,
    left: `${left}px`,
    maxWidth: `${availableWidth}px`,
  };
};

const bindPositionListeners = () => {
  window.addEventListener('resize', updateDropdownPosition);
  window.addEventListener('scroll', updateDropdownPosition, true);

  const content = rootRef.value?.closest('main');
  if (!content || typeof ResizeObserver === 'undefined') return;

  contentBoundsObserver = new ResizeObserver(updateDropdownPosition);
  contentBoundsObserver.observe(content);
};

const unbindPositionListeners = () => {
  window.removeEventListener('resize', updateDropdownPosition);
  window.removeEventListener('scroll', updateDropdownPosition, true);
  contentBoundsObserver?.disconnect();
  contentBoundsObserver = null;
};

watch(open, isOpen => {
  unbindPositionListeners();
  if (!isOpen) {
    panelStyle.value = {};
    return;
  }

  panelStyle.value = { top: '-9999px', left: '-9999px' };
  nextTick(() => {
    updateDropdownPosition();
    bindPositionListeners();
  });
});

onBeforeUnmount(() => {
  unbindPositionListeners();
});

const addFilter = () => {
  lastAppliedQuickFilter.value = null;
  draftFilters.value.push(DEFAULT_FILTER());
};

const removeFilter = index => {
  lastAppliedQuickFilter.value = null;
  if (draftFilters.value.length === 1) {
    draftFilters.value = [DEFAULT_FILTER()];
    return;
  }
  draftFilters.value.splice(index, 1);
};

const isConditionsValid = () => {
  const refs = Array.isArray(conditionsRef.value)
    ? conditionsRef.value
    : [conditionsRef.value].filter(Boolean);
  return refs.every(condition => condition?.validate?.());
};

const applyFilters = () => {
  if (!isConditionsValid()) return;

  emitApplied(draftFilters.value);
  open.value = false;
};

const clearAndApply = () => {
  lastAppliedQuickFilter.value = null;
  draftFilters.value = [DEFAULT_FILTER()];
  emit('update:modelValue', []);
  emit('apply', []);
  open.value = false;
};

const selectQuickFilter = key => {
  if (key === 'assigned_to_me' && !props.currentUser?.id) return;

  const rule = buildQuickFilterRule(key);
  const next = rule ? [rule] : [DEFAULT_FILTER()];

  syncingQuickFilter.value = true;
  lastAppliedQuickFilter.value = key;
  draftFilters.value = next;
  nextTick(() => {
    syncingQuickFilter.value = false;
  });
  emitApplied(next);
  open.value = false;
};

const outsideClickHandler = [
  () => {
    open.value = false;
    emit('close');
  },
  { ignore: ['.events-filter-trigger'] },
];

defineExpose({ toggle, open });
</script>

<template>
  <div ref="rootRef" class="relative shrink-0">
    <ButtonGroup v-if="triggerVariant === 'group'" size="md">
      <ButtonGroupItem
        id="filters"
        v-tooltip.top="t('PLANNING.EVENTS.FILTERS.BUTTON')"
        class="events-filter-trigger"
        :aria-expanded="open"
        aria-haspopup="true"
        :aria-label="t('PLANNING.EVENTS.FILTERS.BUTTON')"
        @click="toggle"
      >
        <span class="flex items-center gap-2">
          <span
            class="i-lucide-filter size-5 align-text-bottom"
            aria-hidden="true"
          />
          <span class="hidden 2xl:inline truncate">{{
            t('PLANNING.EVENTS.FILTERS.BUTTON')
          }}</span>
          <span
            v-if="appliedCount"
            class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-woot-500 text-white text-xs font-semibold"
          >
            {{ appliedCount }}
          </span>
          <span
            class="hidden 2xl:inline i-lucide-chevron-down size-4 align-text-bottom"
            aria-hidden="true"
          />
        </span>
      </ButtonGroupItem>
    </ButtonGroup>
    <Button
      v-else-if="iconOnly"
      v-tooltip.right="t('FILTER.TOOLTIP_LABEL')"
      class="events-filter-trigger"
      icon="i-lucide-list-filter"
      slate
      sm
      faded
      :aria-label="t('FILTER.TOOLTIP_LABEL')"
      @click="toggle"
    />
    <Button
      v-else
      class="events-filter-trigger"
      md
      slate
      outlined
      @click="toggle"
    >
      <template #icon>
        <Icon icon="i-lucide-filter" class="size-5 shrink-0" />
      </template>
      <span class="flex items-center gap-1.5 min-w-0">
        <span class="font-semibold truncate">{{
          t('PLANNING.EVENTS.FILTERS.BUTTON')
        }}</span>
        <span
          v-if="appliedCount"
          class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-woot-500 text-white text-xs font-semibold"
        >
          {{ appliedCount }}
        </span>
        <Icon
          icon="i-lucide-chevron-down"
          class="size-4 shrink-0 text-slate-400"
        />
      </span>
    </Button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="panelRef"
        v-on-click-outside="outsideClickHandler"
        class="fixed z-[9999] w-[min(100vw-2rem,56rem)] overflow-visible border border-n-weak bg-white dark:bg-slate-900 shadow-lg rounded-xl p-4"
        :style="panelStyle"
      >
        <div
          class="grid grid-cols-1 sm:grid-cols-[1fr_14rem] gap-4 items-stretch"
        >
          <div class="flex flex-col gap-4 min-w-0 h-full min-h-full">
            <ul class="grid gap-3 list-none m-0 p-0">
              <template v-for="(filter, index) in draftFilters" :key="index">
                <ConditionRow
                  v-if="index === 0"
                  ref="conditionsRef"
                  v-model:attribute-key="filter.attributeKey"
                  v-model:filter-operator="filter.filterOperator"
                  v-model:values="filter.values"
                  :filter-types="filterTypes"
                  searchable-attributes
                  :show-query-operator="false"
                  @remove="removeFilter(index)"
                />
                <ConditionRow
                  v-else
                  ref="conditionsRef"
                  v-model:attribute-key="filter.attributeKey"
                  v-model:filter-operator="filter.filterOperator"
                  v-model:query-operator="draftFilters[index - 1].queryOperator"
                  v-model:values="filter.values"
                  show-query-operator
                  :filter-types="filterTypes"
                  searchable-attributes
                  @remove="removeFilter(index)"
                />
              </template>
            </ul>

            <div class="flex gap-2 justify-between mt-auto">
              <Button sm ghost blue @click="addFilter">
                {{ t('PLANNING.EVENTS.FILTERS.ADD_RULE') }}
              </Button>
              <div class="flex gap-2">
                <Button sm faded slate @click="clearAndApply">
                  {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
                </Button>
                <Button sm solid blue @click="applyFilters">
                  {{ t('PLANNING.EVENTS.FILTERS.APPLY') }}
                </Button>
              </div>
            </div>
          </div>

          <div
            class="border-t sm:border-t-0 sm:border-l border-n-weak pt-4 sm:pt-0 sm:pl-4"
          >
            <p
              class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2"
            >
              {{ t('PLANNING.EVENTS.FILTERS.QUICK_FILTERS.TITLE') }}
            </p>
            <ul class="grid gap-1 list-none m-0 p-0" role="radiogroup">
              <li v-for="option in quickFilterOptions" :key="option.key">
                <button
                  type="button"
                  role="radio"
                  class="w-full px-3 py-2 text-left text-sm rounded-lg transition-colors"
                  :aria-checked="isQuickFilterActive(option.key)"
                  :class="
                    isQuickFilterActive(option.key)
                      ? 'text-woot-600 dark:text-woot-400 font-medium bg-woot-50 dark:bg-woot-900/20'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  "
                  @click="selectQuickFilter(option.key)"
                >
                  {{ option.label }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
